import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, Route, Wrench } from "lucide-react";

import { useHubToast } from "@/components/ui/HubToast";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { OpenIncidentForm } from "@/components/qc/OpenIncidentForm";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubModuleWriteAny } from "@/lib/useHubModuleWrite";
import {
  QC_INCIDENT_HUB_MODULE_KEYS,
  QC_INCIDENT_QUERY_PARAM,
  readQcIncidentOpenMode,
  writeQcIncidentOpenMode,
  type QcIncidentOpenMode,
} from "@/lib/qc/qcIncidentNav";
import { megaventoryPurchaseOrderDocumentUrl } from "@/lib/megaventoryDocumentUrls";
import { EM_DASH, cn } from "@/lib/utils";
import { useQcIncidentModal } from "@/lib/qc/qcIncidentModalContext";

import { usePottingZplPrinter } from "./pottingZplPrinterStorage";
import { PottingOperatorCartonsPanel, pottingCartonMapKey } from "./PottingOperatorCartonsPanel";
import {
  POTTING_REWORK_CARTONS_QUERY_KEY,
  POTTING_REWORK_QUERY_KEY,
  QC_INCIDENTS_QUERY_KEY,
  assignPottingReworkCarton,
  fetchIncidents,
  fetchPottingReworkCartons,
  fetchPottingReworkQueue,
  printPottingReworkCarton,
  sealPottingReworkCarton,
  type PottingRegradeGrade,
  type PottingReworkQueueItem,
  type PottingReworkSendFlow,
  type QcIncident,
  type QcIncidentResolution,
} from "./qcIncidentsApi";

type Tab = "incidents" | "potting_rework";
type StatusFilter = "unresolved" | "potting_rework" | "rework_failed" | "resolved";

const DEFAULT_STATUS_FILTERS: StatusFilter[] = ["unresolved", "potting_rework", "rework_failed"];
const LIST_SEARCH_DEBOUNCE_MS = 300;
const INCIDENTS_PAGE_SIZE = 100;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}
const STATUS_FILTER_OPTIONS: Array<{ key: StatusFilter; label: string }> = [
  { key: "unresolved", label: "Unresolved" },
  { key: "potting_rework", label: "Potting rework" },
  { key: "rework_failed", label: "Rework failed" },
  { key: "resolved", label: "Resolved" },
];

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function statusTone(incident: QcIncident): BadgeTone {
  if (incident.status === "resolved") return "success";
  if (incident.pottingRework?.regradePottingGrade === "Fail") return "warning";
  if (incident.pottingRework != null) return "info";
  return "neutral";
}

function statusLabel(incident: QcIncident): string {
  if (incident.status === "resolved") return "Resolved";
  if (incident.pottingRework?.regradePottingGrade === "Fail") return "Rework failed";
  if (incident.pottingRework != null) return "Potting rework";
  return "Unresolved";
}

function resolutionLabel(value: QcIncidentResolution | null): string {
  if (value === "repair") return "Repair";
  if (value === "scrap") return "Scrap";
  if (value === "touch_sample") return "Touch sample";
  return EM_DASH;
}

function regradeGradeLabel(value: PottingRegradeGrade | null): string {
  if (value == null) return "Not graded";
  if (value === "Fail") return "Fail";
  return value;
}

function linkedPurchaseOrderCell(row: PottingReworkQueueItem): ReactNode {
  if (row.internalSend) {
    return <span className="text-slate-500">In-house</span>;
  }
  if (row.purchaseOrderMvId != null) {
    const label = row.purchaseOrderMvNumber?.trim() || `MV ${row.purchaseOrderMvId}`;
    return (
      <a
        href={megaventoryPurchaseOrderDocumentUrl(row.purchaseOrderMvId, row.purchaseOrderMvNumber)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 underline decoration-blue-700/40 underline-offset-2 hover:text-blue-900"
        aria-label={`Open purchase order ${label} in Megaventory`}
      >
        <span>{label}</span>
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </a>
    );
  }
  if (row.purchaseOrderId != null) {
    return <span className="font-mono text-xs text-slate-700">{row.purchaseOrderId}</span>;
  }
  return <span className="text-slate-500">{EM_DASH}</span>;
}

function reworkQueueBadgeTone(grade: PottingRegradeGrade | null): BadgeTone {
  if (grade == null) return "muted";
  if (grade === "Fail") return "warning";
  return "success";
}

function incidentTypeLabel(incident: QcIncident): string {
  if (incident.type === "device") return "Device";
  if (incident.type === "clamp") return incident.subtype ? `Clamp (${incident.subtype})` : "Clamp";
  if (incident.type === "pcba") return "PCBA";
  return "Unknown";
}

function isOpenPottingReworkDraft(row: PottingReworkQueueItem): boolean {
  return row.purchaseOrderId == null && row.regradePottingGrade == null && (row.sentAt == null || row.sentAt === "");
}

function reworkSendFlowMatchesRow(flow: PottingReworkSendFlow, row: PottingReworkQueueItem): boolean {
  return flow === "internal_rework" ? row.internalSend : !row.internalSend;
}

function PottingReworkQueue({
  rows,
  writesAllowed,
  onViewIncident,
}: {
  rows: PottingReworkQueueItem[];
  writesAllowed: boolean;
  onViewIncident: (incidentId: number) => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [highlightedCartonKey, setHighlightedCartonKey] = useState<string | null>(null);
  const { station, setStation, selectedPrinter: zplPrinter, unavailableMessage, documents } =
    usePottingZplPrinter();
  const [sealPendingCartonKey, setSealPendingCartonKey] = useState<string | null>(null);
  const [printPendingCartonKey, setPrintPendingCartonKey] = useState<string | null>(null);
  const [assignPendingId, setAssignPendingId] = useState<number | null>(null);
  const [reworkSendFlow, setReworkSendFlow] = useState<PottingReworkSendFlow>("rework");

  const draftReworkCount = useMemo(
    () => rows.filter((row) => isOpenPottingReworkDraft(row) && reworkSendFlowMatchesRow(reworkSendFlow, row)).length,
    [rows, reworkSendFlow],
  );

  const cartonsQuery = useQuery({
    queryKey: [...POTTING_REWORK_CARTONS_QUERY_KEY, reworkSendFlow],
    queryFn: () => fetchPottingReworkCartons(reworkSendFlow),
    refetchInterval: 30_000,
  });

  const assignMutation = useMutation({
    mutationFn: (reworkId: number) => assignPottingReworkCarton(reworkId),
    onMutate: (reworkId) => {
      setAssignPendingId(reworkId);
    },
    onSettled: () => {
      setAssignPendingId(null);
    },
    onSuccess: (data) => {
      setHighlightedCartonKey(pottingCartonMapKey(reworkSendFlow, data.cartonNumber));
      showToast({
        tone: "success",
        message: `Assigned rework #${data.reworkId} to carton ${data.cartonNumber} (${data.productLabel})${
          data.createdNewCarton ? " — new box." : "."
        }`,
      });
      void queryClient.invalidateQueries({ queryKey: POTTING_REWORK_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: POTTING_REWORK_CARTONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const sealMutation = useMutation({
    mutationFn: (cartonNumber: number) => sealPottingReworkCarton(cartonNumber, reworkSendFlow),
    onMutate: (cartonNumber) => {
      setSealPendingCartonKey(pottingCartonMapKey(reworkSendFlow, cartonNumber));
    },
    onSettled: () => {
      setSealPendingCartonKey(null);
    },
    onSuccess: (_data, cartonNumber) => {
      const carton = cartonsQuery.data?.cartons.find((row) => row.cartonNumber === cartonNumber);
      showToast({
        tone: "success",
        message: carton
          ? `Carton ${cartonNumber} sealed (${carton.rowCount} × ${carton.productLabel}). Print its label when ready — it will leave this list after printing.`
          : `Carton ${cartonNumber} sealed. Print its label when ready.`,
      });
      void queryClient.invalidateQueries({ queryKey: POTTING_REWORK_CARTONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: POTTING_REWORK_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const printMutation = useMutation({
    mutationFn: (cartonNumber: number) => printPottingReworkCarton(cartonNumber, zplPrinter.trim(), reworkSendFlow),
    onMutate: (cartonNumber) => {
      setPrintPendingCartonKey(pottingCartonMapKey(reworkSendFlow, cartonNumber));
    },
    onSettled: () => {
      setPrintPendingCartonKey(null);
    },
    onSuccess: (data, cartonNumber) => {
      showToast({
        tone: "success",
        message: `Sent carton ${cartonNumber} label to the printer (${data.lineCount} line group${data.lineCount === 1 ? "" : "s"}).`,
      });
      void queryClient.invalidateQueries({ queryKey: POTTING_REWORK_CARTONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: POTTING_REWORK_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const busy = assignMutation.isPending || sealMutation.isPending || printMutation.isPending;
  const operatorCartons = cartonsQuery.data?.cartons ?? [];

  return (
    <div className="grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
      <PottingOperatorCartonsPanel
        cartons={operatorCartons}
        writesAllowed={writesAllowed}
        busy={busy}
        highlightedCartonKey={highlightedCartonKey}
        sealPendingCartonKey={sealPendingCartonKey}
        printPendingCartonKey={printPendingCartonKey}
        onSeal={(carton) => sealMutation.mutate(carton.cartonNumber)}
        onPrint={(carton) => printMutation.mutate(carton.cartonNumber)}
        zplPrinter={zplPrinter}
        station={station}
        onStationChange={setStation}
        documents={documents}
        unavailableMessage={unavailableMessage}
      />

      <Card>
        <CardHeader>
          <CardTitle>Potting rework queue</CardTitle>
          <div className="mt-1 space-y-1">
            <HoverHintContent hoverHint="Assign each open rework device to a like-with-like carton. SCS rows need a Megaventory purchase order on Potting PO; internal rows use in-house potting send. Record return grades on Final QC or QC Incidents after rework.">
              <Badge tone="muted">Draft rework {draftReworkCount}</Badge>
            </HoverHintContent>
            <div className="mt-2 inline-flex rounded-md border border-slate-300 bg-white p-0.5">
              <button
                type="button"
                className={`rounded px-3 py-1 text-sm font-medium ${
                  reworkSendFlow === "rework" ? "bg-brand-600 text-white" : "text-slate-700"
                }`}
                onClick={() => setReworkSendFlow("rework")}
              >
                SCS cartons
              </button>
              <button
                type="button"
                className={`rounded px-3 py-1 text-sm font-medium ${
                  reworkSendFlow === "internal_rework" ? "bg-brand-600 text-white" : "text-slate-700"
                }`}
                onClick={() => setReworkSendFlow("internal_rework")}
              >
                Internal cartons
              </button>
            </div>
            <p className="text-sm text-slate-500">
              Purchase order create moved to{" "}
              <Link to="/manufacturing-management/potting-po" className="font-medium text-brand-700 hover:underline">
                Potting PO
              </Link>
              .
            </p>
          </div>
        </CardHeader>
        <CardBody className="space-y-3 p-0">
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full border-collapse text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">Serial</th>
                  <th className="px-3 py-2">Send</th>
                  <th className="px-3 py-2">Carton</th>
                  <th className="px-3 py-2">Incident</th>
                  <th className="px-3 py-2">Routed</th>
                  <th className="px-3 py-2">Linked purchase order</th>
                  <th className="px-3 py-2">Return grade</th>
                  <th className="px-3 py-2 text-right">Assign</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const onDraft = isOpenPottingReworkDraft(row);
                  const matchesSendFlow = reworkSendFlowMatchesRow(reworkSendFlow, row);
                  return (
                    <tr key={row.id}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-900">{row.serial ?? EM_DASH}</td>
                      <td className="px-3 py-2 text-slate-700">{row.internalSend ? "Internal" : "SCS"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">
                        {row.cartonNumber ?? EM_DASH}
                        {row.productLabel ? (
                          <span className="mt-0.5 block font-sans text-xs text-slate-500">{row.productLabel}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="font-medium text-sky-800 hover:underline"
                          onClick={() => onViewIncident(row.incidentId)}
                          aria-label={`View QC incident ${row.incidentId}`}
                        >
                          #{row.incidentId}
                        </button>
                        <span className="ml-2 text-slate-500">{row.issue ?? ""}</span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{formatDateTime(row.routedAt)}</td>
                      <td className="px-3 py-2">{linkedPurchaseOrderCell(row)}</td>
                      <td className="px-3 py-2">
                        <Badge tone={reworkQueueBadgeTone(row.regradePottingGrade)}>
                          {regradeGradeLabel(row.regradePottingGrade)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {onDraft && matchesSendFlow ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={!writesAllowed || busy}
                            isLoading={assignPendingId === row.id}
                            onClick={() => assignMutation.mutate(row.id)}
                          >
                            {row.cartonNumber == null ? "Assign carton" : "Re-assign"}
                          </Button>
                        ) : onDraft ? (
                          <span className="text-xs text-slate-500">Use {row.internalSend ? "internal" : "SCS"} cartons</span>
                        ) : (
                          EM_DASH
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-600">No devices are routed to potting rework.</p>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export function QcIncidentsPage() {
  const writesAllowed = useHubModuleWriteAny(QC_INCIDENT_HUB_MODULE_KEYS);
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const { modal, openQcIncident, closeQcIncident } = useQcIncidentModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const incidentSearchInputRef = useRef<HTMLInputElement>(null);
  const focusIncidentSearch = useCallback((): void => {
    window.requestAnimationFrame(() => {
      const input = incidentSearchInputRef.current;
      if (input == null) {
        return;
      }
      input.focus();
      input.select();
    });
  }, []);
  const [tab, setTab] = useState<Tab>("incidents");
  const [showOpenForm, setShowOpenForm] = useState(() => searchParams.get("file") === "1");
  const operatorDismissRef = useRef<(() => void) | null>(null);
  const [search, setSearch] = useState("");
  const [activeStatusFilters, setActiveStatusFilters] = useState<StatusFilter[]>(DEFAULT_STATUS_FILTERS);
  const [pageOffset, setPageOffset] = useState(0);

  const setIncidentQueryParam = useCallback(
    (incidentId: number | null, mode: QcIncidentOpenMode = "resolve") => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (incidentId == null) {
            next.delete(QC_INCIDENT_QUERY_PARAM);
            writeQcIncidentOpenMode(next, null);
          } else {
            next.set(QC_INCIDENT_QUERY_PARAM, String(incidentId));
            writeQcIncidentOpenMode(next, mode);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openIncident = useCallback(
    (incidentId: number, mode: QcIncidentOpenMode = "resolve") => {
      setTab("incidents");
      setShowOpenForm(false);
      setIncidentQueryParam(incidentId, mode);
      openQcIncident(incidentId, mode);
    },
    [openQcIncident, setIncidentQueryParam],
  );

  const closeIncident = useCallback(() => {
    closeQcIncident();
    setIncidentQueryParam(null);
    focusIncidentSearch();
  }, [closeQcIncident, focusIncidentSearch, setIncidentQueryParam]);

  useEffect(() => {
    if (tab !== "incidents" || showOpenForm || modal != null) {
      return;
    }
    focusIncidentSearch();
  }, [tab, showOpenForm, modal, focusIncidentSearch]);

  const debouncedSearch = useDebouncedValue(search.trim(), LIST_SEARCH_DEBOUNCE_MS);
  const incidentsQuery = useQuery({
    queryKey: [...QC_INCIDENTS_QUERY_KEY, debouncedSearch, activeStatusFilters, pageOffset],
    queryFn: () =>
      fetchIncidents({
        search: debouncedSearch,
        status: activeStatusFilters,
        limit: INCIDENTS_PAGE_SIZE,
        offset: pageOffset,
      }),
  });
  const pottingReworkQuery = useQuery({
    queryKey: POTTING_REWORK_QUERY_KEY,
    queryFn: fetchPottingReworkQueue,
  });

  const incidents = incidentsQuery.data?.rows ?? [];
  const incidentsTotal = incidentsQuery.data?.total ?? 0;
  const pottingRows = pottingReworkQuery.data?.rows ?? [];

  useEffect(() => {
    setPageOffset(0);
  }, [debouncedSearch, activeStatusFilters]);

  useEffect(() => {
    const raw = searchParams.get(QC_INCIDENT_QUERY_PARAM)?.trim();
    if (raw == null || raw === "") {
      if (modal != null) {
        closeQcIncident();
      }
      return;
    }
    const incidentId = Number.parseInt(raw, 10);
    if (!Number.isFinite(incidentId) || incidentId <= 0) {
      return;
    }
    const mode = readQcIncidentOpenMode(searchParams);
    setTab("incidents");
    setShowOpenForm(false);
    if (modal?.incidentId !== incidentId) {
      openQcIncident(incidentId, mode);
    }
  }, [searchParams, modal, openQcIncident, closeQcIncident]);

  const counts = useMemo(() => {
    const summary = incidentsQuery.data?.summaryCounts;
    return {
      open: summary?.open ?? 0,
      routed: summary?.routed ?? 0,
      resolved: summary?.resolved ?? 0,
      reworkWaiting: pottingRows.filter((row) => row.regradePottingGrade == null).length,
    };
  }, [incidentsQuery.data?.summaryCounts, pottingRows]);

  const statusFilterCounts = incidentsQuery.data?.statusCounts ?? {
    unresolved: 0,
    potting_rework: 0,
    rework_failed: 0,
    resolved: 0,
  };

  const pageStart = incidentsTotal === 0 ? 0 : pageOffset + 1;
  const pageEnd = Math.min(pageOffset + incidents.length, incidentsTotal);
  const canGoPrevious = pageOffset > 0;
  const canGoNext = pageOffset + INCIDENTS_PAGE_SIZE < incidentsTotal;

  const statusFiltersMatchDefault = useMemo(() => {
    if (activeStatusFilters.length !== DEFAULT_STATUS_FILTERS.length) return false;
    const active = new Set(activeStatusFilters);
    return DEFAULT_STATUS_FILTERS.every((value) => active.has(value));
  }, [activeStatusFilters]);

  const incidentsFiltersActive = debouncedSearch !== "" || !statusFiltersMatchDefault;

  const incidentTableColumns = useMemo((): HubDataTableColumn<QcIncident>[] => {
    return [
      {
        id: "id",
        header: "Id",
        sortKey: "id",
        overflow: "nowrap",
        sortValue: (row) => row.id,
        cell: (row) => (
          <button
            type="button"
            className="font-medium text-sky-800 hover:underline"
            onClick={() => openIncident(row.id, "view")}
            aria-label={`View QC incident ${row.id}`}
          >
            #{row.id}
          </button>
        ),
      },
      {
        id: "serial",
        header: "Serial",
        sortKey: "serial",
        overflow: "nowrap",
        sortValue: (row) => row.serial ?? "",
        cell: (row) => <span className="font-mono text-xs text-slate-900">{row.serial ?? EM_DASH}</span>,
      },
      {
        id: "type",
        header: "Type",
        sortKey: "type",
        sortValue: (row) => incidentTypeLabel(row),
        cell: (row) => incidentTypeLabel(row),
      },
      {
        id: "issue",
        header: "Issue",
        sortKey: "issue",
        overflow: "truncate",
        sortValue: (row) => row.issue ?? "",
        cell: (row) => row.issue ?? EM_DASH,
      },
      {
        id: "issueDate",
        header: "Issue date",
        sortKey: "issueDate",
        overflow: "nowrap",
        sortValue: (row) => row.issueDate ?? "",
        cell: (row) => formatDate(row.issueDate),
      },
      {
        id: "status",
        header: "Status",
        sortKey: "status",
        sortValue: (row) => statusLabel(row),
        cell: (row) => <Badge tone={statusTone(row)}>{statusLabel(row)}</Badge>,
      },
      {
        id: "resolution",
        header: "Resolution",
        sortKey: "resolution",
        sortValue: (row) => row.repairScrap ?? "",
        cell: (row) => resolutionLabel(row.repairScrap),
      },
      {
        id: "actions",
        header: "Actions",
        align: "right",
        sortValue: () => "",
        cell: (row) => (
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => openIncident(row.id, "loan")}>
              Analysis loan
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => openIncident(row.id, row.status === "resolved" ? "view" : "resolve")}
            >
              {row.status === "resolved" ? "View resolution" : "Resolve"}
            </Button>
          </div>
        ),
      },
    ];
  }, [openIncident]);

  const loadError =
    incidentsQuery.error instanceof Error
      ? incidentsQuery.error.message
      : pottingReworkQuery.error instanceof Error
        ? pottingReworkQuery.error.message
        : null;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-brand-600" aria-hidden />
            <h1 className="text-xl font-semibold text-slate-900">QC incidents</h1>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            SGS-only incident handling for device, clamp, and PCBA issues. Opening an incident is separate from resolving
            it; potting rework is tracked as a route and final resolution remains repair or scrap.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            void queryClient.invalidateQueries({ queryKey: QC_INCIDENTS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: POTTING_REWORK_QUERY_KEY });
          }}
          disabled={incidentsQuery.isFetching || pottingReworkQuery.isFetching}
        >
          <RefreshCw
            className={cn("h-4 w-4", incidentsQuery.isFetching || pottingReworkQuery.isFetching ? "animate-spin" : "")}
            aria-hidden
          />
          Refresh
        </Button>
      </header>

      {loadError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Could not load QC incidents: {loadError}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardBody className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Open</p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{counts.open}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-3">
            <Route className="h-5 w-5 text-blue-500" aria-hidden />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Routed</p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{counts.routed}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-3">
            <Wrench className="h-5 w-5 text-violet-500" aria-hidden />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Waiting regrade</p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{counts.reworkWaiting}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" aria-hidden />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Resolved</p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{counts.resolved}</p>
            </div>
          </CardBody>
        </Card>
      </div>

      <div
        className="flex flex-wrap gap-1 border-b border-slate-200 pb-px"
        role="tablist"
        aria-label="QC incidents sections"
      >
        {(["incidents", "potting_rework"] satisfies Tab[]).map((value) => {
          const selected = tab === value;
          const tabId = value === "incidents" ? "qc-incidents-tab-incidents" : "qc-incidents-tab-potting-rework";
          const panelId = value === "incidents" ? "qc-incidents-panel-incidents" : "qc-incidents-panel-potting-rework";
          return (
            <button
              key={value}
              type="button"
              role="tab"
              id={tabId}
              aria-selected={selected}
              aria-controls={panelId}
              className={cn(
                "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
                selected
                  ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
                  : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
              )}
              onClick={() => setTab(value)}
            >
              {value === "incidents" ? "Incidents" : "Potting rework queue"}
            </button>
          );
        })}
      </div>

      {tab === "incidents" ? (
        <div role="tabpanel" id="qc-incidents-panel-incidents" aria-labelledby="qc-incidents-tab-incidents">
          <div className="space-y-4">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-900">Incidents</h2>
              <ReadOnlyHoverLayer
                active={!writesAllowed}
                hoverHint="Your role has read-only access to the Manufacturing hub module."
              >
                <Button
                  type="button"
                  size="sm"
                  disabled={!writesAllowed}
                  onClick={() => {
                    setShowOpenForm(true);
                    closeIncident();
                  }}
                >
                  New incident
                </Button>
              </ReadOnlyHoverLayer>
            </header>

            <Card>
              <CardHeader>
                <CardTitle>Filters</CardTitle>
              </CardHeader>
              <CardBody className="space-y-3">
                <Input
                  ref={incidentSearchInputRef}
                  id="qc-incidents-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search serial (includes device history), issue, person..."
                  autoComplete="off"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</span>
                  {STATUS_FILTER_OPTIONS.map((option) => {
                    const active = activeStatusFilters.includes(option.key);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() =>
                          setActiveStatusFilters((current) =>
                            current.includes(option.key)
                              ? current.filter((value) => value !== option.key)
                              : [...current, option.key],
                          )
                        }
                        className={cn(
                          "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                          active
                            ? "border-brand-300 bg-brand-50 text-brand-800"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                        )}
                      >
                        <span>{option.label}</span>
                        <span
                          className={cn(
                            "rounded-full px-1.5 py-0.5 text-xs tabular-nums",
                            active ? "bg-brand-100 text-brand-800" : "bg-slate-100 text-slate-600",
                          )}
                        >
                          {statusFilterCounts[option.key]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </CardBody>
            </Card>

            <HubDataTable
              title={
                incidentsQuery.isLoading
                  ? "Loading incidents…"
                  : incidentsTotal === 0
                    ? "Incidents"
                    : `Showing ${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${incidentsTotal.toLocaleString()}`
              }
              headerExtra={
                incidentsTotal > INCIDENTS_PAGE_SIZE ? (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!canGoPrevious || incidentsQuery.isFetching}
                      onClick={() => setPageOffset((current) => Math.max(0, current - INCIDENTS_PAGE_SIZE))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!canGoNext || incidentsQuery.isFetching}
                      onClick={() => setPageOffset((current) => current + INCIDENTS_PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </div>
                ) : null
              }
              columns={incidentTableColumns}
              rows={incidents}
              rowKey={(row) => row.id}
              tableClassName="min-w-[1040px]"
              loading={incidentsQuery.isLoading}
              loadingMessage="Loading incidents…"
              error={
                incidentsQuery.isError && incidentsQuery.error instanceof Error
                  ? incidentsQuery.error.message
                  : null
              }
              empty="No incidents yet."
              isFiltered={incidentsFiltersActive}
              filteredEmpty="No incidents match these filters."
            />
          </div>
          {showOpenForm ? (
            <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 px-4 py-6">
              <div className="w-full max-w-2xl">
                <Card>
                  <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <CardTitleWithHelp hoverHint="This opens the quality event and its NCR. Resolution stays on the separate Resolve screen.">
                      Open quality event (manufacturing)
                    </CardTitleWithHelp>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (operatorDismissRef.current != null) {
                          operatorDismissRef.current();
                          return;
                        }
                        setShowOpenForm(false);
                      }}
                    >
                      Close
                    </Button>
                  </CardHeader>
                  <CardBody>
                    <OpenIncidentForm
                      dismissRef={operatorDismissRef}
                      onCancel={() => setShowOpenForm(false)}
                      onSuccess={(incident, notice) => {
                        setShowOpenForm(false);
                        closeIncident();
                        void queryClient.invalidateQueries({ queryKey: QC_INCIDENTS_QUERY_KEY });
                        focusIncidentSearch();
                        const eventLabel =
                          incident.qualityEventId != null ? `event ${incident.qualityEventId}` : "the quality event";
                        showToast({
                          tone: "success",
                          message: notice ?? `Opened ${eventLabel} and NCR ${incident.id}.`,
                        });
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div role="tabpanel" id="qc-incidents-panel-potting-rework" aria-labelledby="qc-incidents-tab-potting-rework">
          <PottingReworkQueue
            rows={pottingRows}
            writesAllowed={writesAllowed}
            onViewIncident={(incidentId) => openIncident(incidentId, "view")}
          />
        </div>
      )}
    </div>
  );
}
